<template>
  <!-- Search and filters button (Mobile) -->
  <div class="filters-mobile" v-if="isMobile" :class="{'is-open': showFilters}">
    <SearchInputComponent class="filters-mobile_input" 
                          placeholder="Selecciona" 
                          label="distancia"
                          v-model="pCode"
                          :initialValue="(store.getters.postalCode) ? store.getters.postalCode : FilterDefaultPostalCode" 
                          @search="searchMobile"
                          @updateValue="updateFiltersHandler"/>
    <button class="btn-notification btn-secondary-m btn-icon-underline-black" @click="toggleFilters" >
      <i class="bp-icon-Filters">
        <span v-if="numberFilters!==0" class="filter-notification">{{ numberFilters }}</span>
      </i>
      <span>Ver filtros</span>
    </button>
  </div>

  <!-- Filters container -->
  <div class="filter-container" :class="{ 'is-mobile': isMobile, 'is-open': isMobile && showFilters }">
    <!-- Filters close btn (Mobile) -->
    <div class="btn-back-container" v-if="isMobile">
      <button class="close-button" @click="toggleFilters">
        <i class="bp-icon-Back"></i>
        <span>volver</span>
      </button>
    </div>

    <h3 class="filter-container-title" v-if="isMobile">Filtrar por:</h3>

    <!-- Filters content -->
    <transition-group name="fade" tag="ul" :class="['filter-container-list', { 'is-mobile': isMobile}]">
      <li v-for="filter in filters.slice(0, visibleFiltersCount)" :key="filter.id">
        <SearchInputComponent v-if='filter.type==="text-search"'
                              :placeholder="filter.placeholder"
                              :label="filter.label"
                              :id="filter.id"
                              :initialValue="filter.value"
                              v-model="filter.value"
                              @search="searchMobile"
                              @updateValue="updateFiltersHandler" />

        <SliderSelectorComponent v-else-if="filter.type==='slider'"
                                 :placeholder="filter.placeholder"
                                 :minValue="filter.min"
                                 :maxValue="filter.max"
                                 :unit="filter.unit"
                                 :label="filter.label"
                                 v-model="filter.value"
                                 :initialValue="filter.initialValue"
                                 :id="filter.id"
                                 @updateSlider="updateFiltersHandler"/>
        <DropdownMultiSelectComponent v-else-if="filter.type==='dropdown-multiselect'"
                                      :id="filter.id"
                                      :options="filter.options"
                                      :model-value="Array.isArray(filter.value) ? filter.value : []"
                                      :placeholder="filter.placeholder"
                                      :label="filter.label"
                                      @updateFilters="updateFiltersHandler"/>
        <InputNumberComponent v-else-if="filter.type==='numeric-input'"
                              :placeholder="filter.placeholder"
                              :label="filter.label"
                              v-model="filter.value"
                              :id="filter.id"
                              @updateNumber="updateFiltersHandler" />
        <DatePickerComponent v-else-if="filter.type==='date-selector'"
                             v-model="filter.value"
                             :label="filter.label"
                             :minDate="filter.minDate"
                             :id="filter.id"
                             @update:modelValue="updateFiltersHandler" />
      </li>
    </transition-group>

    <div class="btn-container">
      <div v-if="!isMobile">
        <button class="btn-toggle btn-secondary-m btn-icon-underline-black" @click="toggleAdvancedFilters">
          <i :class="['bp-icon-Chevron', { 'active': allFiltersVisible }]"></i>
          <span>{{ (allFiltersVisible) ? 'ocultar' : 'ver'}} filtros avanzados</span>
        </button>
      </div>
      <div>
        <button class="btn-secondary-m btn-underline-black" @click="clearData">borrar filtros</button>
        <button class="btn-primary-m btn-primary-green" @click="submit">aplicar filtros</button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineProps, defineEmits, watch } from 'vue';
import { Filter, FilterValue } from "@/models/frontend/FiltersModels";
import SearchInputComponent from '@/components/core/fields/SearchInput/SearchInputComponent.vue';
import SliderSelectorComponent from '@/components/core/fields/SliderSelector/SliderSelectorComponent.vue';
import DropdownMultiSelectComponent from '@/components/core/fields/DropdownMultiSelect/DropdownMultiSelectComponent.vue';
import InputNumberComponent from '@/components/core/fields/InputNumber/InputNumberComponent.vue';
import DatePickerComponent from '@/components/core/fields/DatePicker/DatePickerComponent.vue';
import { useStore } from 'vuex';
import { FilterDefaultPostalCode } from '@/beepallet.consts';
import { isMobile, isTablet } from '@/helpers/CommonHelper';

const store = useStore();
const props = defineProps<{ 
  filters: Filter[],
  numberFilters:number
 }>();
const emit = defineEmits(['applyFilters', 'clearFilters', 'update:filters']);

const showFilters = ref(false); // Estado para mostrar u ocultar filtros en móvil
const visibleFiltersCount = ref(6);
const allFiltersVisible = ref(false);
const previousValue = ref<'MOBILE' | 'TABLET' | 'DESKTOP'>('DESKTOP');

//Propiedad para rellenar el código postal en móvil
const pCode = ref<string>('');

const searchMobile = (data:string) => {
  const filter = props.filters.find((filter) => filter.id === 'pcode');
  if (filter) filter.value = data;
  updateFiltersHandler('pcode', data)
  emit("applyFilters");
}

const handleFilterListView = () => {
  if(isTablet.value){
    visibleFiltersCount.value = 5;
    if(previousValue.value!='TABLET'){
      allFiltersVisible.value = false;
    }
  }
  else if(!isTablet.value && !isMobile.value){
    visibleFiltersCount.value = 6;
    if(previousValue.value!='DESKTOP'){
      allFiltersVisible.value = false;
    }
  }
  else if(isMobile.value){
    visibleFiltersCount.value =  props.filters.length;
    allFiltersVisible.value = true;
  }
};

// Alternar el estado de los filtros en móvil
const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const toggleAdvancedFilters = () => {
  if(!isMobile.value){
      if (!allFiltersVisible.value) {
        visibleFiltersCount.value = props.filters.length;
    } else {
        visibleFiltersCount.value = (isTablet.value)? 5 : 6;
    }
      allFiltersVisible.value = !allFiltersVisible.value;
    } 
}

// Agregar event listener para manejar el cambio de tamaño de ventana
onMounted(() => {
  previousValue.value = (isTablet.value)?'TABLET':(isMobile.value)?'MOBILE':'DESKTOP';
  handleFilterListView();
  window.addEventListener('resize', handleFilterListView);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleFilterListView);
});

const submit = () => {
  emit("applyFilters");
  toggleFilters();
}

const clearData = () => {
  emit('clearFilters')
}

// **Actualizar los filtros sin modificar `props.filters` directamente**
const updateFiltersHandler = (id: string, value: FilterValue) => {
  const filter = props.filters.find((filter) => filter.id === id);

  if (id === "pcode") {
    store.dispatch('updatePostalCode', value);
  }
  if (filter) filter.value = value;
  emit("update:filters", props.filters);
};

// Sincronizar localFilters con props.filters cuando cambia
watch(() => props.filters, (newFilters) => {
  const filter = newFilters.find((filter) => filter.id === 'pcode');
  if (filter?.value) pCode.value = filter.value as string;
}, { deep: true, immediate: true });

</script>

<style lang="scss" scoped>
@import 'FiltersComponent';
</style>